import { apiRequest, HttpError } from '@/shared/api';
import { LEGAL_UPDATED } from 'legal';
import { env } from '@/shared/config';
import { reachGoal, reportClientId } from './metrika';

/**
 * Сессия веб-версии (см. WEB.md). Токен выдаёт бэкенд — гостю при первом
 * открытии (`POST /auth/guest`), позже при входе через провайдера, — и он
 * живёт в localStorage. В мини-аппе не используется: там initData.
 */
const TOKEN_KEY = 'auth:session';

export function readSessionToken(): string | undefined {
  try {
    return localStorage.getItem(TOKEN_KEY) ?? undefined;
  } catch {
    return undefined;
  }
}

export function saveSessionToken(token: string): void {
  try {
    localStorage.setItem(TOKEN_KEY, token);
  } catch {
    // Запись запрещена (приватный режим) — сессия проживёт до закрытия вкладки
    // в памяти: `sessionAuthorization` ниже читает и её.
  }
  memoryToken = token;
}

export function clearSessionToken(): void {
  memoryToken = undefined;
  try {
    localStorage.removeItem(TOKEN_KEY);
  } catch {
    // См. saveSessionToken.
  }
}

/**
 * Что в localStorage принадлежит человеку, а не устройству: прогресс
 * онбординга (его ответы уезжают на сервер только в конце), начатая оплата,
 * недавние темы рулетки. Новый гость или выход — и следующий человек в этом
 * браузере не должен начать с чужого шага онбординга. Ключи — из
 * `features/onboarding/model/storage.ts`, `PaywallOffer`, `TopicRouletteSheet`.
 */
const PERSONAL_KEY_PREFIXES = ['onboarding:', 'paywall:', 'topicRoulette.'];

function clearPersonalState(): void {
  try {
    for (const key of Object.keys(localStorage)) {
      if (PERSONAL_KEY_PREFIXES.some((prefix) => key.startsWith(prefix))) localStorage.removeItem(key);
    }
  } catch {
    // Хранилище недоступно — и чужого состояния в нём нет.
  }
}

/**
 * Браузер переходит на другой аккаунт (вход переехал на аккаунт из бота, см.
 * `useTelegramLink`): прогресс онбординга гостя ему чужой. Дальше вызывающий
 * перезапускает приложение с корня.
 */
export function switchSession(token: string): void {
  clearPersonalState();
  saveSessionToken(token);
}

/**
 * Аккаунт только что появился при входе — спросить, не занимается ли человек
 * уже в боте (`TelegramAskSheet`). Флаг на вкладку: вопрос задаётся один раз.
 */
const ASK_TELEGRAM_KEY = 'auth:ask-telegram';

export function takeAskTelegram(): boolean {
  try {
    const value = sessionStorage.getItem(ASK_TELEGRAM_KEY);
    sessionStorage.removeItem(ASK_TELEGRAM_KEY);
    return value === '1';
  } catch {
    return false;
  }
}

/** Запасная копия на случай, когда localStorage недоступен. */
let memoryToken: string | undefined;

/** Значение заголовка `Authorization` для `setAuthorizationProvider`. */
export function sessionAuthorization(): string | undefined {
  const token = readSessionToken() ?? memoryToken;
  return token ? `Bearer ${token}` : undefined;
}

/** Только то, что бэкенд пропустит в метке `/start`: `A–Z a–z 0–9 _`, части через `-`. */
function sanitize(value: string): string {
  return value.replace(/[^A-Za-z0-9_]+/g, '_').replace(/^_+|_+$/g, '');
}

/**
 * Метка источника из адреса — в формате параметра `/start` бота, чтобы
 * бэкенд разобрал её тем же `parseStartPayload`. Лендинг может передать
 * готовую метку как `?start=` (ту же, что сейчас уходит в бота, с `-ym<ClientID>`);
 * реклама — обычными UTM: `utm_source=yandex&utm_campaign=level_test` →
 * `web_yandex_level_test`. Без меток — просто `web`.
 */
export function startPayloadFromUrl(search: string = window.location.search): string {
  const params = new URLSearchParams(search);
  const start = params.get('start');
  if (start && /^[A-Za-z0-9_-]{1,64}$/.test(start)) return start;
  const parts = ['web', params.get('utm_source'), params.get('utm_campaign')]
    .map((part) => (part ? sanitize(part) : ''))
    .filter(Boolean);
  return parts.join('_').slice(0, 64);
}

function browserLocale(): string {
  return navigator.language.toLowerCase().startsWith('ru') ? 'ru' : 'en';
}

/**
 * Пришли по рекламной ссылке (или с лендинга с готовой меткой): таких сразу
 * ведём в онбординг гостем, без экрана входа (см. `prepareSession`).
 */
export function isAdVisit(search: string = window.location.search): boolean {
  const params = new URLSearchParams(search);
  return [...params.keys()].some((name) => name === 'start' || name === 'yclid' || name.startsWith('utm_'));
}

/** Гость с меткой источника из адреса — кнопка «Начать» или рекламная ссылка. */
/** Гость заведён в этой загрузке страницы — он новый, а не вернувшийся. */
let guestCreatedNow = false;

export function isGuestCreatedNow(): boolean {
  return guestCreatedNow;
}

/**
 * Браузер пришёл микрофоном со статьи — онбординг ему не показываем вовсе
 * (решение 2026-10-28): человек пришёл поговорить на тему, а не проходить
 * тест. Приложение открыто ему и без онбординга; согласие на ПДн и оферту
 * он даёт входом или галочками на пейволле. Метка — на браузер: гость и так
 * живёт в одном браузере.
 */
const LANDING_VISITOR_KEY = 'landing:visitor';

export function markLandingVisitor(): void {
  try {
    localStorage.setItem(LANDING_VISITOR_KEY, '1');
  } catch {
    // Без хранилища — после разговора человек попадёт в онбординг, как раньше.
  }
}

export function isLandingVisitor(): boolean {
  try {
    return localStorage.getItem(LANDING_VISITOR_KEY) === '1';
  } catch {
    return false;
  }
}

export async function createGuest(): Promise<void> {
  const { token } = await apiRequest<{ token: string }>('/auth/guest', {
    method: 'POST',
    body: { start: startPayloadFromUrl(), locale: browserLocale() },
  });
  clearPersonalState();
  saveSessionToken(token);
  guestCreatedNow = true;
  reportClientId();
}

/**
 * Только dev: `?devUser=<id>` — войти под существующим пользователем
 * (`POST /auth/dev/session`), чтобы смотреть в браузере всё приложение, а не
 * только онбординг гостя.
 */
async function devLogin(): Promise<boolean> {
  if (!env.isDev) return false;
  const url = new URL(window.location.href);
  const userId = Number(url.searchParams.get('devUser'));
  if (!Number.isInteger(userId) || userId <= 0) return false;
  // Один раз: убираем из адреса, иначе каждая перезагрузка заводила бы новую сессию.
  url.searchParams.delete('devUser');
  window.history.replaceState(window.history.state, '', url);
  const { token } = await apiRequest<{ token: string }>('/auth/dev/session', {
    method: 'POST',
    body: { userId },
  });
  saveSessionToken(token);
  return true;
}

/**
 * Вход через Яндекс ID (см. WEB.md, `auth/yandex.ts` на бэкенде): браузер
 * уходит на страницу Яндекса и возвращается на `YANDEX_CALLBACK_PATH` с
 * `code`. `state` — случайная строка в sessionStorage: колбэк с чужим или
 * подложенным кодом (вход жертвы в аккаунт злоумышленника) её не знает.
 */
export const YANDEX_CALLBACK_PATH = '/auth/callback/yandex';
const PENDING_LOGIN_KEY = 'auth:pending-login';
const LOGIN_FAILED_KEY = 'auth:login-failed';

export const VK_CALLBACK_PATH = '/auth/callback/vk';

/**
 * «Заниматься без VPN» из мини-аппа: `app.boltaem.chat/connect?code=…`
 * (см. `auth/webConnect.ts` на бэкенде). Код лежит в sessionStorage до входа
 * и уходит вместе с ним — вход ложится на телеграмный аккаунт кода.
 */
export const CONNECT_PATH = '/connect';
const CONNECT_CODE_KEY = 'auth:connect';

/** Открыли `/connect?code=…` — запомнить код и убрать его из адреса. */
function captureConnectCode(): void {
  const code = new URLSearchParams(window.location.search).get('code');
  try {
    if (code && /^[A-Za-z0-9_-]{22}$/.test(code)) sessionStorage.setItem(CONNECT_CODE_KEY, code);
  } catch {
    // Без sessionStorage вход пройдёт как обычный, без привязки к боту.
  }
  window.history.replaceState(null, '', CONNECT_PATH);
}

function readConnectCode(): string | undefined {
  try {
    return sessionStorage.getItem(CONNECT_CODE_KEY) ?? undefined;
  } catch {
    return undefined;
  }
}

function dropConnectCode(): void {
  try {
    sessionStorage.removeItem(CONNECT_CODE_KEY);
  } catch {
    // Нечего стирать.
  }
}

interface PendingLogin {
  state: string;
  /** Код «Заниматься без VPN» на момент клика (`CONNECT_PATH`). */
  connect?: string;
  /** PKCE VK ID: секрет, хеш которого ушёл в `authorize`; у Яндекса его нет. */
  codeVerifier?: string;
  /** Метка источника на момент клика — после возврата от Яндекса UTM в адресе уже нет. */
  start: string;
}

export function startYandexLogin(clientId: string): void {
  const state = crypto.randomUUID();
  const pending: PendingLogin = { state, start: startPayloadFromUrl(), connect: readConnectCode() };
  try {
    sessionStorage.setItem(PENDING_LOGIN_KEY, JSON.stringify(pending));
  } catch {
    // Без sessionStorage колбэк не сверит state и вход не завершится —
    // человек увидит «не получилось войти».
  }
  const url = new URL('https://oauth.yandex.ru/authorize');
  url.search = new URLSearchParams({
    response_type: 'code',
    client_id: clientId,
    redirect_uri: `${window.location.origin}${YANDEX_CALLBACK_PATH}`,
    state,
  }).toString();
  window.location.assign(url.toString());
}

/** Случайная строка из алфавита base64url — для `state` и `code_verifier` VK ID. */
function randomToken(bytes: number): string {
  return base64Url(crypto.getRandomValues(new Uint8Array(bytes)));
}

function base64Url(data: Uint8Array): string {
  return btoa(String.fromCharCode(...data)).replace(/\+/g, '-').replace(/\//g, '_').replace(/=+$/, '');
}

/**
 * Вход через VK ID (см. `auth/vk.ts` на бэкенде): OAuth 2.1 с PKCE.
 * `code_verifier` — 43 символа, лежит в sessionStorage рядом со `state`,
 * в адрес уходит только его SHA-256. `state` — не короче 32 символов, как
 * требует VK.
 */
export async function startVkLogin(clientId: string): Promise<void> {
  const state = randomToken(32);
  const codeVerifier = randomToken(32);
  const challenge = base64Url(
    new Uint8Array(await crypto.subtle.digest('SHA-256', new TextEncoder().encode(codeVerifier))),
  );
  const pending: PendingLogin = {
    state,
    codeVerifier,
    start: startPayloadFromUrl(),
    connect: readConnectCode(),
  };
  try {
    sessionStorage.setItem(PENDING_LOGIN_KEY, JSON.stringify(pending));
  } catch {
    // См. `startYandexLogin`.
  }
  const url = new URL('https://id.vk.ru/authorize');
  url.search = new URLSearchParams({
    response_type: 'code',
    client_id: clientId,
    redirect_uri: `${window.location.origin}${VK_CALLBACK_PATH}`,
    state,
    code_challenge: challenge,
    code_challenge_method: 'S256',
    scope: 'vkid.personal_info email',
  }).toString();
  window.location.assign(url.toString());
}

function takePendingLogin(): PendingLogin | null {
  try {
    const raw = sessionStorage.getItem(PENDING_LOGIN_KEY);
    sessionStorage.removeItem(PENDING_LOGIN_KEY);
    return raw ? (JSON.parse(raw) as PendingLogin) : null;
  } catch {
    return null;
  }
}

/**
 * Вход не удался (не отказ на странице Яндекса, а ошибка) — экран входа
 * покажет об этом строку. Флаг стирается при первом чтении, а ответ
 * запоминается до перезагрузки: на экране входа кнопки рисуются дважды
 * (раскладка ноутбука и шторка телефона), и строку должны увидеть обе.
 */
/**
 * Почему не вышло: `failed` — общий сбой; со страницы «Заниматься без VPN» —
 * `connect_expired` (ссылка из бота устарела или уже использована) и
 * `identity_taken` (этот Яндекс/VK у другого аккаунта с оплатами) и
 * `web_login_taken` (у аккаунта из бота уже свой вход, второй не привязать).
 */
export type LoginFailure = 'failed' | 'connect_expired' | 'identity_taken' | 'web_login_taken';

let loginFailure: LoginFailure | null | undefined;

export function readLoginFailure(): LoginFailure | null {
  if (loginFailure !== undefined) return loginFailure;
  try {
    const value = sessionStorage.getItem(LOGIN_FAILED_KEY);
    sessionStorage.removeItem(LOGIN_FAILED_KEY);
    loginFailure =
      value === 'connect_expired' || value === 'identity_taken' || value === 'web_login_taken'
        ? value
        : value
          ? 'failed'
          : null;
  } catch {
    loginFailure = null;
  }
  return loginFailure;
}

export function didLoginFail(): boolean {
  return readLoginFailure() !== null;
}

/**
 * Вернулись от Яндекса или VK. Код обменивается на сессию; гостевой токен уходит
 * заголовком, и бэкенд привязывает вход к гостю. Дальше — всегда корень
 * (решение владельца 2026-10-28), а оттуда `RootRedirect` ведёт на главную
 * или, если онбординг не пройден, в него. Не туда, откуда уходили: вход мог
 * попасть в другой, уже существующий аккаунт, и разговор или экран гостя
 * ему чужой — открылась бы ошибка. Адрес колбэка с кодом из истории убирается.
 */
async function completeProviderLogin(provider: 'yandex' | 'vk'): Promise<void> {
  const params = new URLSearchParams(window.location.search);
  const pending = takePendingLogin();
  const code = params.get('code');
  // Вход со страницы «Заниматься без VPN» при сбое возвращается на неё же.
  window.history.replaceState(null, '', pending?.connect ? CONNECT_PATH : '/');

  // `error=access_denied` — человек нажал «Отмена» у провайдера: просто назад.
  if (params.get('error') === 'access_denied') return;
  if (!code || !pending || params.get('state') !== pending.state) {
    markLoginFailed();
    return;
  }
  const body =
    provider === 'yandex'
      ? { code }
      : {
          code,
          codeVerifier: pending.codeVerifier,
          deviceId: params.get('device_id'),
          state: pending.state,
          redirectUri: `${window.location.origin}${VK_CALLBACK_PATH}`,
        };
  try {
    const { token, switched, isNew } = await apiRequest<{ token: string; switched: boolean; isNew?: boolean }>(`/auth/${provider}`, {
      method: 'POST',
      // `legalVersion` — редакция документов, которые вход принимает (строка
      // под кнопками, `AuthOptions`): бэкенд пишет согласие в журнал.
      body: {
        ...body,
        start: pending.start,
        locale: browserLocale(),
        connect: pending.connect,
        legalVersion: LEGAL_UPDATED,
      },
    });
    if (pending.connect) {
      dropConnectCode();
      window.history.replaceState(null, '', '/');
    }
    // Вошли в уже существующий аккаунт — прогресс онбординга гостя в
    // localStorage ему чужой.
    if (switched) clearPersonalState();
    saveSessionToken(token);
    // Новый аккаунт — может, человек уже занимается в боте и его прогресс там.
    if (isNew) {
      try {
        sessionStorage.setItem(ASK_TELEGRAM_KEY, '1');
      } catch {
        // Без sessionStorage вопроса не будет — привязка остаётся в профиле.
      }
    }
    reachGoal('app_login', { provider });
  } catch (error) {
    // Сам сбой запроса уже ушёл в отчёт об ошибках из транспорта.
    const reason: LoginFailure =
      error instanceof HttpError && error.status === 410
        ? 'connect_expired'
        : error instanceof HttpError && error.status === 409
          ? error.body.includes('web_login_taken')
            ? 'web_login_taken'
            : 'identity_taken'
          : 'failed';
    // Код «без VPN» сервер уже сжёг — новый берут в боте.
    if (reason !== 'failed') dropConnectCode();
    markLoginFailed(reason);
  }
}

function markLoginFailed(reason: LoginFailure = 'failed'): void {
  try {
    sessionStorage.setItem(LOGIN_FAILED_KEY, reason);
  } catch {
    // Строки об ошибке не будет — человек просто снова на экране входа.
  }
}

/**
 * Перед первым рендером веб-версии. true — сессия есть (или только что
 * заведена гостю из рекламы), можно грузить приложение; false — человек
 * открыл адрес сам, без входа: показываем экран входа (`features/web-auth`).
 */
export async function prepareSession(): Promise<boolean> {
  if (window.location.pathname === YANDEX_CALLBACK_PATH) await completeProviderLogin('yandex');
  if (window.location.pathname === VK_CALLBACK_PATH) await completeProviderLogin('vk');
  if (await devLogin()) return true;
  // «Заниматься без VPN»: экран входа с привязкой к боту — даже если в
  // браузере уже есть сессия (гость или другой вход), её заменит вход по коду.
  if (window.location.pathname === CONNECT_PATH) {
    captureConnectCode();
    return false;
  }
  if (sessionAuthorization()) return true;
  if (!isAdVisit()) return false;
  await createGuest();
  return true;
}

/**
 * 401 по сессии — она истекла или удалена (выход на другом устройстве):
 * забываем токен и начинаем с экрана входа.
 */
export function dropSessionOnUnauthorized(error: unknown): never {
  if (error instanceof HttpError && error.status === 401) {
    clearSessionToken();
    window.location.replace('/');
  }
  throw error;
}

let sessionLost = false;

/**
 * Сессию удалили или она истекла посреди работы (выход на другом
 * устройстве, отвязка входа) — 401 `Invalid session` на любом запросе, а не
 * только на `/me` при запуске: забываем токен и уходим на экран входа. Так же
 * и без токена вовсе (`Missing credentials`).
 * true — ошибка обработана здесь: её и все сбои после неё (оборванные
 * перезагрузкой запросы — «нет сети») в служебный чат не шлём. До 2026-10-28
 * каждый такой запрос падал в чат отдельным отчётом, а человек оставался
 * на экране с ошибками.
 */
export function handleLostSession(error: unknown): boolean {
  if (sessionLost) return true;
  // `Missing credentials` — токена нет вовсе: его стёрли в другой вкладке
  // (выход, вход другим аккаунтом — localStorage у вкладок общий), а эта
  // ещё открыта и шлёт запросы.
  if (
    !(
      error instanceof HttpError &&
      error.status === 401 &&
      (error.body.includes('Invalid session') || error.body.includes('Missing credentials'))
    )
  ) {
    return false;
  }
  sessionLost = true;
  clearSessionToken();
  window.location.replace('/');
  return true;
}

/** Выход: сессия удаляется на сервере, токен — здесь, и снова экран входа. */
export async function logout(): Promise<void> {
  await apiRequest('/auth/logout', { method: 'POST' }).catch(() => undefined);
  clearSessionToken();
  clearPersonalState();
  window.location.replace('/');
}
